import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { FC } from 'react';
import { vars } from '../../../reactive';
import { encodeText, getBranchCurrencySymbol } from '../../../utils';
import FormContainer from '../../Shared/FormContainer/FormContainer';
import { ErrorMessage } from '../../Shared/Shared';
import {
  CustomizationItems,
  CustomizationPrice,
  CustomizationTitle,
  FormItemContainer,
  FormItemDescription,
  FormLabel,
  FormRadioCustomizationsContainer,
  FormRadioWrapper,
  MainCustomizationsTitle,
  PriceTitleContainer,
  Title
} from '../styled';
import { FORM_RADIO_COMPONENT_TYPES } from '../types';

interface FormRadioCustomizationsProps {
  type: keyof typeof FORM_RADIO_COMPONENT_TYPES;
  formOptions: Record<string, any>;
  titlesArray: string[];
  customizations: {
    id: string;
    name: string;
    price: number;
    Indicator: false | '' | JSX.Element;
    title: string;
    meta: Record<string, string>;
    description?: string;
    isPerNight?: boolean;
  }[][];
  onSubmit: (data: Record<string, string>) => void;
  borderColor?: string;
  loading?: boolean;
  isLoggedIn?: boolean;
}

const FormRadioCustomizations: FC<FormRadioCustomizationsProps> = ({ type, formOptions, titlesArray, customizations, onSubmit, borderColor = 'gray', loading, isLoggedIn }) => {
  const { t } = useTranslation();
  const title = t(FORM_RADIO_COMPONENT_TYPES[type].title);
  const registerAs = FORM_RADIO_COMPONENT_TYPES[type].registerAs;
  const requiredMessage = t(FORM_RADIO_COMPONENT_TYPES[type].requiredMessage);
  const { register, handleSubmit, formState } = formOptions;
  const selectedProduct = useReactiveVar(vars.selectedProduct);

  return (
    <FormRadioWrapper isLoggedIn={isLoggedIn!}>
      <FormContainer onSubmit={handleSubmit(onSubmit)} title={title} loading={loading} error={formState?.errors?.[registerAs]} scrollable>
        <FormRadioCustomizationsContainer>
          {titlesArray?.map((title, index) => (
            <CustomizationTitle key={index}>
              <MainCustomizationsTitle>{title}</MainCustomizationsTitle>
              <CustomizationItems>
                {customizations[index].map(({ title: customizationTitle, price, description, isPerNight }, customizationsIndex) => {
                  const perNightSuffix = isPerNight ? t('formRadioCustomizations.perNight', ' / Night') : '';
                  const priceString = selectedProduct?.show_customizations_price ? (!!price && price > 0 ? `+ ${getBranchCurrencySymbol()}${price}${perNightSuffix}` : '') : '';

                  return (
                    <FormLabel key={customizationTitle} borderColor={borderColor}>
                      <input
                        {...register(`${registerAs}.${encodeText(title)}`, {
                          required: requiredMessage
                        })}
                        type="radio"
                        value={customizationTitle}
                        autoFocus
                        defaultChecked={customizationsIndex === 0}
                      />
                      <FormItemContainer disabled={false} small>
                        <PriceTitleContainer noMargin>
                          <Title>{customizationTitle}</Title>
                          <CustomizationPrice>{priceString}</CustomizationPrice>
                        </PriceTitleContainer>
                        <FormItemDescription
                          dangerouslySetInnerHTML={{
                            __html: description
                          }}
                        />
                      </FormItemContainer>
                    </FormLabel>
                  );
                })}
              </CustomizationItems>
            </CustomizationTitle>
          ))}
        </FormRadioCustomizationsContainer>
        {!!Object.values(formState?.errors?.[registerAs] || {})?.length && <ErrorMessage>{Object.values(formState?.errors?.[registerAs] || {})?.[0].message}</ErrorMessage>}
      </FormContainer>
    </FormRadioWrapper>
  );
};
export default FormRadioCustomizations;
